import { useEffect, useState } from "react";
import client, { fmt } from "@/lib/api";
import { Panel } from "@/components/Layout";
import { UserPlus, Award, Zap, Crown } from "lucide-react";
import { toast } from "sonner";

const SPECIALTIES = ["Paid Media", "SEO", "Social", "Content", "Brand", "Influencer", "Email", "Analytics"];

export default function Onboarding() {
    const [agents, setAgents] = useState([]);
    const [form, setForm] = useState({
        full_name: "", email: "", phone: "",
        legacy_company: "Primerica",
        specialty: "Paid Media",
        years_experience: 0, book_size: 0,
    });

    const load = async () => {
        const { data } = await client.get("/agents");
        setAgents(data);
    };
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.full_name || !form.email) return toast.error("Name and email required");
        try {
            await client.post("/agents/onboard", {
                ...form,
                years_experience: parseInt(form.years_experience) || 0,
                book_size: parseFloat(form.book_size) || 0,
            });
            toast.success("Defection transmitted — Admiral notified");
            setForm({ full_name: "", email: "", phone: "", legacy_company: "Primerica", specialty: "Paid Media", years_experience: 0, book_size: 0 });
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail?.[0]?.msg || "Submission failed");
        }
    };

    return (
        <div className="space-y-6" data-testid="onboarding-page">
            <section className="hud-panel p-8 lg:p-12 relative overflow-hidden grid-bg fade-up" data-testid="onboard-hero">
                <div className="hud-corner-tl" /><div className="hud-corner-tr" />
                <div className="hud-corner-bl" /><div className="hud-corner-br" />
                <div className="relative z-10 max-w-3xl">
                    <div className="label-tag mb-3 text-gold">/// FORCED-ENTRY ONBOARDING — LEGACY DEFECTORS</div>
                    <h1 className="heading-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mb-4">
                        Trade <span className="text-gold">25%</span> for <span className="text-gold">60%</span>.<br />
                        Trade pencils for <span className="text-gold">automation</span>.
                    </h1>
                    <p className="text-[#aaa] text-sm sm:text-base max-w-2xl mb-6">
                        Legacy operators — break the chain. EIGOO Marketing offers a <span className="text-gold font-bold">60% share</span>,
                        industrial-grade automation, and direct line to the Cross-Fleet bridge. No quotas. No politics. Pure throughput.
                    </p>
                    <div className="grid grid-cols-3 gap-4 max-w-xl">
                        <Pill label="Share" value="60%" icon={Crown} />
                        <Pill label="Onboarding" value="48hr" icon={Zap} />
                        <Pill label="Floor" value="$0" icon={Award} />
                    </div>
                </div>
            </section>

            <div className="grid lg:grid-cols-[1.1fr_1fr] gap-6">
                <Panel tag="// DEFECTION APPLICATION" title="Submit Your Hull" testId="onboard-form-panel">
                    <form onSubmit={submit} className="space-y-3" data-testid="onboard-form">
                        <Field label="Full Name">
                            <input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="hud-input" data-testid="onb-name" />
                        </Field>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Email">
                                <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="hud-input" data-testid="onb-email" />
                            </Field>
                            <Field label="Phone">
                                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="hud-input" data-testid="onb-phone" />
                            </Field>
                        </div>
                        <Field label="Legacy Company">
                            <input value={form.legacy_company} onChange={(e) => setForm({ ...form, legacy_company: e.target.value })} className="hud-input" data-testid="onb-legacy" />
                        </Field>
                        <div className="grid grid-cols-3 gap-3">
                            <Field label="Specialty">
                                <select value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} className="hud-input" data-testid="onb-specialty">
                                    {SPECIALTIES.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </Field>
                            <Field label="Years Exp">
                                <input type="number" value={form.years_experience} onChange={(e) => setForm({ ...form, years_experience: e.target.value })} className="hud-input" data-testid="onb-years" />
                            </Field>
                            <Field label="Book Size ($)">
                                <input type="number" value={form.book_size} onChange={(e) => setForm({ ...form, book_size: e.target.value })} className="hud-input" data-testid="onb-book" />
                            </Field>
                        </div>
                        <button className="btn-gold w-full" type="submit" data-testid="onb-submit">Submit Defection</button>
                        <style>{`.hud-input { background:#050505; border:1px solid #332A0C; padding:10px 12px; width:100%; color:#F5F5F5; font-family:'Montserrat',sans-serif; outline:none; } .hud-input:focus { border-color:#D4AF37; }`}</style>
                    </form>
                </Panel>

                <Panel tag={`// ${agents.length} DEFECTORS`} title="Recent Applications" testId="agents-list">
                    <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1">
                        {agents.map(a => (
                            <div key={a.id} className="border border-gold-edge p-3" data-testid={`agent-${a.id}`}>
                                <div className="flex justify-between items-start">
                                    <div>
                                        <div className="font-black uppercase tracking-tight">{a.full_name}</div>
                                        <div className="label-tag text-[#888] mt-1">
                                            {a.specialty} {a.legacy_company && `· from ${a.legacy_company}`}
                                        </div>
                                    </div>
                                    <span className={`label-tag px-2 py-1 border ${a.status === "active" ? "border-[#34C759] text-[#34C759]" : a.status === "verified" ? "border-gold text-gold" : "border-white/30 text-white/60"}`}>{a.status}</span>
                                </div>
                                <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                                    <div><span className="label-tag">Years</span> <span className="mono ml-1 text-white">{a.years_experience}</span></div>
                                    <div><span className="label-tag">Book</span> <span className="mono ml-1 text-white">{fmt.money(a.book_size)}</span></div>
                                </div>
                                <div className="label-tag mt-2 text-[#666]">{a.email}</div>
                            </div>
                        ))}
                        {agents.length === 0 && (
                            <div className="text-center py-6">
                                <Award className="w-8 h-8 text-gold mx-auto mb-2" />
                                <div className="label-tag">No defectors yet</div>
                            </div>
                        )}
                    </div>
                </Panel>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="label-tag block mb-1">{label}</label>
            {children}
        </div>
    );
}

function Pill({ label, value, icon: Icon }) {
    return (
        <div className="border border-gold-edge p-3 text-center">
            {Icon && <Icon className="w-4 h-4 text-gold mx-auto mb-1" />}
            <div className="label-tag">{label}</div>
            <div className="metric-value text-2xl text-gold mt-1">{value}</div>
        </div>
    );
}
